import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, AlertTriangle, Globe, DollarSign, TrendingUp, TrendingDown, ArrowRight, Save, Trash2, Play, Edit, ChevronDown, ChevronRight, RefreshCw, ArrowLeft, Building2, Shield, BarChart3, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { getRiskBand } from '@/lib/utils';
import { NavBar } from '@/components/NavBar';

const COUNTRY_OPTIONS = [
  { code: 'CN', name: 'China' }, { code: 'ID', name: 'Indonesia' }, { code: 'VN', name: 'Vietnam' },
  { code: 'JP', name: 'Japan' }, { code: 'KR', name: 'South Korea' }, { code: 'IN', name: 'India' },
  { code: 'TH', name: 'Thailand' }, { code: 'MY', name: 'Malaysia' }, { code: 'PH', name: 'Philippines' },
  { code: 'TW', name: 'Taiwan' }, { code: 'SG', name: 'Singapore' }, { code: 'BD', name: 'Bangladesh' },
  { code: 'MM', name: 'Myanmar' }, { code: 'KH', name: 'Cambodia' }, { code: 'PK', name: 'Pakistan' },
  { code: 'LK', name: 'Sri Lanka' }, { code: 'PG', name: 'Papua New Guinea' },
];

const CATEGORY_ICONS = { 'Trade policy': Globe, 'Natural disaster': AlertTriangle, 'Geopolitical': Shield, 'Economic': DollarSign };
const CATEGORY_COLORS = { 'Trade policy': '#0097A7', 'Natural disaster': '#FF3B30', 'Geopolitical': '#8B0000', 'Economic': '#FF9500' };

const ScenarioPage = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('landing'); // landing, builder, results
  const [templates, setTemplates] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [activeScenario, setActiveScenario] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // Builder state
  const [scenarioName, setScenarioName] = useState('');
  const [selectedCountries, setSelectedCountries] = useState({});
  const [sectorModifier, setSectorModifier] = useState({});
  const [delayWeeks, setDelayWeeks] = useState('2-4 weeks');
  const [freightImpact, setFreightImpact] = useState(10);
  const [currencyImpact, setCurrencyImpact] = useState(0);
  const [costImpact, setCostImpact] = useState(5);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [t, s] = await Promise.all([api.getScenarioTemplates(), api.getSavedScenarios()]);
      setTemplates(t.data);
      setSaved(s.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const resetBuilder = () => {
    setScenarioName(''); setSelectedCountries({}); setSectorModifier({});
    setDelayWeeks('2-4 weeks'); setFreightImpact(10); setCurrencyImpact(0); setCostImpact(5);
  };

  const loadTemplate = (t) => {
    setScenarioName(t.name);
    setSelectedCountries(t.params.countries || {});
    setSectorModifier(t.params.sector_modifier || {});
    setDelayWeeks(t.params.delay_weeks || '2-4 weeks');
    setFreightImpact(t.params.freight_impact_pct || 0);
    setCurrencyImpact(t.params.currency_impact_pct || 0);
    setCostImpact(t.params.cost_impact_pct || 0);
    setActiveScenario({ ...t, isTemplate: true });
    setView('builder');
  };

  const runScenario = async () => {
    setRunning(true);
    try {
      const params = { countries: selectedCountries, sector_modifier: sectorModifier, delay_weeks: delayWeeks, freight_impact_pct: freightImpact, currency_impact_pct: currencyImpact, cost_impact_pct: costImpact };
      const res = await api.runScenario({ name: scenarioName || 'Custom scenario', params });
      setResults(res.data);
      setActiveScenario(prev => ({ ...(prev || {}), name: scenarioName || 'Custom scenario', params, results_summary: res.data.summary }));
      setView('results');
    } catch (e) { toast.error('Failed to run scenario'); console.error(e); }
    finally { setRunning(false); }
  };

  const saveCurrentScenario = async () => {
    try {
      await api.saveScenario({
        name: activeScenario?.name || scenarioName || 'Custom scenario',
        template_id: activeScenario?.id || null,
        params: { countries: selectedCountries, sector_modifier: sectorModifier, delay_weeks: delayWeeks, freight_impact_pct: freightImpact, currency_impact_pct: currencyImpact, cost_impact_pct: costImpact },
        results_summary: results?.summary || {},
      });
      toast.success('Scenario saved');
      loadData();
    } catch (e) { toast.error('Failed to save'); }
  };

  const deleteScenario = async (id) => {
    try { await api.deleteScenario(id); toast.success('Deleted'); setSaved(s => s.filter(x => x.id !== id)); setDeleteId(null); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const rerunSaved = async (s) => {
    setScenarioName(s.name);
    setSelectedCountries(s.params?.countries || {});
    setSectorModifier(s.params?.sector_modifier || {});
    setDelayWeeks(s.params?.delay_weeks || '2-4 weeks');
    setFreightImpact(s.params?.freight_impact_pct || 0);
    setCurrencyImpact(s.params?.currency_impact_pct || 0);
    setCostImpact(s.params?.cost_impact_pct || 0);
    setActiveScenario(s);
    setView('builder');
  };

  const fmtSpend = (v) => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : `$${Math.round(v)}`;

  // ─── LANDING VIEW ───
  if (view === 'landing') return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <NavBar />
      <main className="pt-24 px-4 lg:px-6 pb-8"><div className="max-w-[1200px] mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 mb-6">
          <h1 className="text-3xl font-bold text-[#0D3B4F]" data-testid="scenarios-title">What if?</h1>
          <p className="text-[#4E6974] mt-1">Model supply chain risk scenarios — test how potential disruptions would affect your suppliers, risk scores, and costs.</p>
        </motion.div>

        {/* Saved scenarios */}
        {saved.length > 0 && (
          <div className="mb-8">
            <h2 className="text-base font-bold text-[#0D3B4F] mb-3">Your saved scenarios</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {saved.map(s => (
                <Card key={s.id} className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid={`saved-scenario-${s.id}`}>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-bold text-[#0D3B4F] mb-1">{s.name}</h3>
                    <p className="text-[10px] text-[#4E6974] mb-2">{new Date(s.created_at).toLocaleDateString()}</p>
                    {s.results_summary && (
                      <div className="text-xs text-[#4E6974] space-y-0.5">
                        <p>{s.results_summary.affected_count || 0} suppliers affected</p>
                        {s.results_summary.total_cost_impact > 0 && <p className="text-[#FF9500]">Est. impact: {fmtSpend(s.results_summary.total_cost_impact)}</p>}
                      </div>
                    )}
                    <div className="flex gap-2 mt-3">
                      <Button variant="outline" size="sm" onClick={() => rerunSaved(s)}><RefreshCw className="h-3 w-3 mr-1" />Re-run</Button>
                      <Button variant="ghost" size="sm" className="text-[#FF3B30]" onClick={() => setDeleteId(s.id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Templates */}
        <h2 className="text-base font-bold text-[#0D3B4F] mb-3">Start from a template</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
          {['Trade policy', 'Natural disaster', 'Geopolitical', 'Economic'].map(cat => {
            const Icon = CATEGORY_ICONS[cat] || Globe;
            const color = CATEGORY_COLORS[cat] || '#4E6974';
            const catTemplates = templates.filter(t => t.category === cat);
            return catTemplates.map(t => (
              <Card key={t.id} className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none hover:shadow-[0_8px_32px_rgba(0,0,0,0.1)] cursor-pointer transition-all group"
                onClick={() => loadTemplate(t)} data-testid={`template-${t.id}`}>
                <CardContent className="p-4 flex gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
                    <Icon className="h-5 w-5" style={{ color }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-bold text-[#0D3B4F] group-hover:text-[#0097A7] transition-colors">{t.name}</h3>
                      <Badge variant="secondary" className="text-[9px]">{cat}</Badge>
                    </div>
                    <p className="text-xs text-[#4E6974] leading-relaxed">{t.description}</p>
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-[#4E6974]">
                      {Object.keys(t.params.countries).length > 0 && <span>{Object.keys(t.params.countries).length} countries</span>}
                      {t.params.delay_weeks !== 'N/A' && <span>Delay: {t.params.delay_weeks}</span>}
                      {t.params.cost_impact_pct > 0 && <span>Cost: +{t.params.cost_impact_pct}%</span>}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-[#E0E0E0] group-hover:text-[#0097A7] transition-colors flex-shrink-0 mt-2" />
                </CardContent>
              </Card>
            ));
          })}
        </div>

        {/* Build your own */}
        <Card className="bg-[#0D3B4F] text-white rounded-lg shadow-lg cursor-pointer hover:opacity-95 transition-all" onClick={() => { resetBuilder(); setActiveScenario(null); setView('builder'); }} data-testid="build-own-btn">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center"><Zap className="h-6 w-6 text-[#4DD0E1]" /></div>
            <div>
              <h3 className="text-lg font-bold">Build your own scenario</h3>
              <p className="text-white/70 text-sm">Create a custom scenario from scratch — choose countries, sectors, and disruption parameters</p>
            </div>
          </CardContent>
        </Card>
      </div></main>

      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent><DialogHeader><DialogTitle>Delete scenario?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#4E6974]">This cannot be undone.</p>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button><Button className="bg-[#FF3B30] text-white" onClick={() => deleteScenario(deleteId)}>Delete</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  // ─── BUILDER VIEW ───
  if (view === 'builder') return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <NavBar />
      <main className="pt-16 px-6 pb-8"><div className="max-w-[800px] mx-auto">
        <Button variant="ghost" onClick={() => { setView('landing'); setResults(null); }} className="mt-6 text-[#4E6974]"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-4 space-y-5">
          {/* Step 1: Name */}
          <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
            <CardContent className="p-5">
              <h3 className="text-sm font-bold text-[#0D3B4F] mb-3">Step 1: Name your scenario</h3>
              <Input value={scenarioName} onChange={e => setScenarioName(e.target.value)} placeholder="e.g. China trade war escalation Q3 2026" data-testid="scenario-name-input" />
            </CardContent>
          </Card>
          {/* Step 2: Countries */}
          <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
            <CardContent className="p-5">
              <h3 className="text-sm font-bold text-[#0D3B4F] mb-3">Step 2: Select affected countries</h3>
              <div className="space-y-2">
                {COUNTRY_OPTIONS.map(c => {
                  const active = c.code in selectedCountries;
                  return (
                    <div key={c.code} className="flex items-center gap-3">
                      <Button variant={active ? 'default' : 'outline'} size="sm" className={`w-[140px] justify-start ${active ? 'bg-[#00707C] text-white' : ''}`}
                        onClick={() => setSelectedCountries(prev => active ? Object.fromEntries(Object.entries(prev).filter(([k]) => k !== c.code)) : { ...prev, [c.code]: 15 })}
                        data-testid={`country-toggle-${c.code.toLowerCase()}`}>
                        {c.name}
                      </Button>
                      {active && (
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-xs text-[#4E6974] w-16">+{selectedCountries[c.code]} pts</span>
                          <Slider value={[selectedCountries[c.code]]} min={5} max={50} step={5}
                            onValueChange={([v]) => setSelectedCountries(prev => ({ ...prev, [c.code]: v }))}
                            className="flex-1" data-testid={`country-slider-${c.code.toLowerCase()}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          {/* Step 3: Disruption params */}
          <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
            <CardContent className="p-5">
              <h3 className="text-sm font-bold text-[#0D3B4F] mb-3">Step 3: Set disruption parameters</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-[#4E6974]">Estimated supply delay</Label>
                  <Select value={delayWeeks} onValueChange={setDelayWeeks}>
                    <SelectTrigger data-testid="delay-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="N/A">No delay expected</SelectItem>
                      <SelectItem value="1 week">1 week</SelectItem>
                      <SelectItem value="1-2 weeks">1-2 weeks</SelectItem>
                      <SelectItem value="2-4 weeks">2-4 weeks</SelectItem>
                      <SelectItem value="1-2 months">1-2 months</SelectItem>
                      <SelectItem value="3+ months">3+ months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#4E6974]">Freight rate impact: +{freightImpact}%</Label>
                  <Slider value={[freightImpact]} min={0} max={100} step={5} onValueChange={([v]) => setFreightImpact(v)} data-testid="freight-slider" />
                </div>
                <div>
                  <Label className="text-xs text-[#4E6974]">Currency impact (AUD): {currencyImpact > 0 ? '+' : ''}{currencyImpact}%</Label>
                  <Slider value={[currencyImpact]} min={-15} max={15} step={1} onValueChange={([v]) => setCurrencyImpact(v)} data-testid="currency-slider" />
                </div>
                <div>
                  <Label className="text-xs text-[#4E6974]">Commodity cost impact: +{costImpact}%</Label>
                  <Slider value={[costImpact]} min={0} max={50} step={1} onValueChange={([v]) => setCostImpact(v)} data-testid="cost-slider" />
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Run button */}
          <Button className="w-full bg-[#0D3B4F] text-white h-12 text-base" onClick={runScenario} disabled={running || Object.keys(selectedCountries).length === 0} data-testid="run-scenario-btn">
            {running ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Modelling...</> : <><Play className="h-4 w-4 mr-2" />Model this scenario</>}
          </Button>
        </motion.div>
      </div></main>
    </div>
  );

  // ─── RESULTS VIEW ───
  if (view === 'results' && results) {
    const s = results.summary;
    const sevColor = s.severity === 'severe' ? '#FF3B30' : s.severity === 'moderate' ? '#FF9500' : '#2E7D32';
    const sevBg = s.severity === 'severe' ? 'bg-[#FFF0F0]' : s.severity === 'moderate' ? 'bg-[#FFF8F0]' : 'bg-[#F0FFF4]';
    return (
      <div className="min-h-screen bg-[#F5F5F5]">
        <NavBar />
        <main className="pt-16 px-6 pb-8"><div className="max-w-[1100px] mx-auto">
          <div className="flex items-center justify-between mt-6">
            <Button variant="ghost" onClick={() => setView('builder')} className="text-[#4E6974]"><ArrowLeft className="h-4 w-4 mr-1" />Edit scenario</Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={saveCurrentScenario} data-testid="save-scenario-btn"><Save className="h-4 w-4 mr-1" />Save scenario</Button>
              <Button variant="outline" size="sm" onClick={() => { setView('landing'); setResults(null); }}>New scenario</Button>
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-4 space-y-5">
            {/* Impact Summary Banner */}
            <Card className={`${sevBg} rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none border-l-4`} style={{ borderLeftColor: sevColor }} data-testid="impact-banner">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-[#0D3B4F] mb-2">{s.scenario_name}</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <p className="text-xs text-[#4E6974]">Suppliers affected</p>
                    <p className="text-2xl font-bold text-[#0D3B4F]">{s.affected_count} <span className="text-sm font-normal text-[#4E6974]">of {s.total_suppliers}</span></p>
                    <p className="text-xs text-[#4E6974]">across {s.countries_affected} countries</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#4E6974]">Portfolio risk score</p>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-[#0D3B4F]">{s.current_portfolio_score}</span>
                      <ArrowRight className="h-4 w-4 text-[#4E6974]" />
                      <span className="text-2xl font-bold" style={{ color: sevColor }}>{s.projected_portfolio_score}</span>
                      <Badge style={{ backgroundColor: sevColor, color: 'white' }}>+{s.score_change}</Badge>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-[#4E6974]">Estimated cost impact</p>
                    <p className="text-2xl font-bold" style={{ color: sevColor }}>{s.cost_impact_range}</p>
                    <p className="text-xs text-[#4E6974]">based on {fmtSpend(s.total_spend)} annual spend</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Supplier Impact Table */}
            {results.affected_suppliers.length > 0 && (
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardHeader><CardTitle className="text-base font-bold text-[#0D3B4F]">Supplier impact ({results.affected_suppliers.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="impact-supplier-table">
                      <thead><tr className="border-b border-[#E0E0E0]">
                        <th className="text-left py-2 px-2 text-[#4E6974] font-medium text-xs">Supplier</th>
                        <th className="text-left py-2 px-2 text-[#4E6974] font-medium text-xs">Country</th>
                        <th className="text-center py-2 px-2 text-[#4E6974] font-medium text-xs">Current</th>
                        <th className="text-center py-2 px-2 text-[#4E6974] font-medium text-xs">Projected</th>
                        <th className="text-center py-2 px-2 text-[#4E6974] font-medium text-xs">Level change</th>
                        <th className="text-right py-2 px-2 text-[#4E6974] font-medium text-xs">Spend</th>
                        <th className="text-right py-2 px-2 text-[#4E6974] font-medium text-xs">Cost impact</th>
                        <th className="text-center py-2 px-2 text-[#4E6974] font-medium text-xs">Action</th>
                      </tr></thead>
                      <tbody>
                        {results.affected_suppliers.map((a, i) => {
                          const cb = getRiskBand(a.current_score);
                          const pb = getRiskBand(a.projected_score);
                          const actionColor = a.action_needed === 'Yes' ? '#FF3B30' : a.action_needed === 'Monitor' ? '#FF9500' : '#2E7D32';
                          return (
                            <tr key={a.id || `impact-${i}`} className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA] cursor-pointer" onClick={() => navigate(`/suppliers/${a.id}`)} data-testid={`impact-row-${i}`}>
                              <td className="py-2.5 px-2 font-medium text-[#0D3B4F]">{a.supplier_name}</td>
                              <td className="py-2.5 px-2 text-[#4E6974] text-xs">{a.country_name}</td>
                              <td className="py-2.5 px-2 text-center"><Badge className={`${cb.bg} ${cb.text} text-[10px]`}>{a.current_score}</Badge></td>
                              <td className="py-2.5 px-2 text-center"><Badge className={`${pb.bg} ${pb.text} text-[10px]`}>{a.projected_score}</Badge></td>
                              <td className="py-2.5 px-2 text-center text-xs">
                                {a.level_changed ? <span className="text-[#FF3B30] font-medium">{a.current_level} → {a.projected_level}</span> : <span className="text-[#4E6974]">{a.current_level}</span>}
                              </td>
                              <td className="py-2.5 px-2 text-right text-xs text-[#4E6974]">{fmtSpend(a.annual_spend_aud)}</td>
                              <td className="py-2.5 px-2 text-right text-xs font-medium" style={{ color: a.cost_impact > 0 ? '#FF3B30' : '#4E6974' }}>{a.cost_impact > 0 ? fmtSpend(a.cost_impact) : '-'}</td>
                              <td className="py-2.5 px-2 text-center"><Badge style={{ backgroundColor: `${actionColor}15`, color: actionColor }} className="text-[10px]">{a.action_needed}</Badge></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Country Impact Breakdown */}
            {results.country_impacts.length > 0 && (
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardHeader><CardTitle className="text-base font-bold text-[#0D3B4F]">Country impact breakdown</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {results.country_impacts.map((ci, i) => {
                      const cb = getRiskBand(ci.avg_current);
                      const pb = getRiskBand(ci.avg_projected);
                      return (
                        <div key={ci.country_code || `ci-${i}`} className="p-4 bg-[#FAFAFA] rounded-lg" data-testid={`country-impact-${ci.country_code?.toLowerCase()}`}>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-bold text-[#0D3B4F]">{ci.country_name}</h4>
                            <div className="flex items-center gap-1">
                              <Badge className={`${cb.bg} ${cb.text} text-[10px]`}>{ci.avg_current}</Badge>
                              <ArrowRight className="h-3 w-3 text-[#4E6974]" />
                              <Badge className={`${pb.bg} ${pb.text} text-[10px]`}>{ci.avg_projected}</Badge>
                            </div>
                          </div>
                          <p className="text-xs text-[#4E6974]">{ci.suppliers} supplier{ci.suppliers !== 1 ? 's' : ''} &middot; {fmtSpend(ci.total_spend)} at risk</p>
                          <p className="text-xs text-[#4E6974] mt-2 leading-relaxed">
                            If this scenario materialises, your {ci.suppliers} supplier{ci.suppliers !== 1 ? 's' : ''} in {ci.country_name} representing {fmtSpend(ci.total_spend)} would move to {pb.level.toLowerCase()} risk.
                            {ci.avg_projected >= 70 ? ' Immediate action required.' : ci.avg_projected >= 50 ? ' Monitor closely and review contingency plans.' : ' Continue routine monitoring.'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recommendations */}
            {results.recommendations.length > 0 && (
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardHeader><CardTitle className="text-base font-bold text-[#0D3B4F]">Recommended actions</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {results.recommendations.map((r, i) => (
                      <div key={i} className="flex gap-3 p-3 bg-[#E8F5E9] rounded-lg">
                        <span className="text-sm font-bold text-[#2E7D32] flex-shrink-0">{i + 1}.</span>
                        <p className="text-sm text-[#0D3B4F]">{r}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </div></main>
      </div>
    );
  }

  return null;
};

export default ScenarioPage;
